import classnames from 'classnames';
import React, { useState } from 'react';
import { trackNavigationLinkClicked } from '../../tracking/events';
import { Spinner } from './Spinner';
import styles from './Link.module.scss';
import { ButtonBaseProps, ButtonStateProps, ButtonStyleProps, ButtonTrackingProps } from './buttons/Button';

interface LinkProps extends Omit<ButtonBaseProps, 'text'>, ButtonTrackingProps, ButtonStateProps, ButtonStyleProps {
  href: string;
  text?: string;
  tabIndex?: number;
  callback?: (e: React.MouseEvent) => void;
  target?: '_blank' | '_self' | '_parent' | 'top';
  id?: string;
  ariaLabelledby?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
}

export const Link: React.FC<LinkProps> = ({
  href,
  dataTrackLabel,
  dataTrackLocation,
  children,
  text,
  size,
  buttonType,
  target,
  noMargin,
  id,
  ariaLabelledby,
  onMouseLeave,
  onMouseEnter,
  onFocus,
  disabled,
  fullWidth,
  labelStyleOptions,
  tabIndex,
}) => {
  const linkType = buttonType ?? 'link';
  const linkSize = size ?? 'medium';
  const [loading, setLoading] = useState<boolean>(false);
  const [hovering, setHovering] = useState<boolean>(false);

  const onMouseEnterLink = () => {
    setHovering(true);
    onMouseEnter?.();
  };

  const onMouseLeaveLink = () => {
    setHovering(false);
    onMouseLeave?.();
  };

  const optionalStyles: React.CSSProperties = {};
  if (labelStyleOptions?.color && !hovering && !disabled) {
    optionalStyles.color = labelStyleOptions?.color;
  }
  if (fullWidth) {
    optionalStyles.width = '100%';
  }
  if (noMargin) {
    optionalStyles.margin = 'unset';
  }

  return (
    <a
      className={classnames(styles.root, {
        [styles.link]: linkType === 'link',
        [styles.underlineLabel]: linkType === 'link' && labelStyleOptions?.underline,
        [styles.uppercaseLabel]: linkType === 'link' && labelStyleOptions?.uppercase,
        [styles.loading]: loading,
        [styles.small]: linkSize === 'small',
        [styles.medium]: linkSize === 'medium',
        [styles.large]: linkSize === 'large',
        [styles.primary]: linkType === 'primary',
        [styles.secondary]: linkType === 'secondary',
        [styles.transparent]: linkType === 'transparent',
        [styles.ghost]: linkType === 'ghost',
      })}
      style={optionalStyles}
      role="button"
      tabIndex={tabIndex ?? 0}
      onClick={() => {
        if (linkType !== 'link' && linkType !== 'ghost') {
          setLoading(true);
        }
        if (dataTrackLabel && dataTrackLocation && !disabled) {
          trackNavigationLinkClicked(dataTrackLabel, dataTrackLocation);
        }
      }}
      target={target}
      href={href}
      id={id}
      aria-labelledby={ariaLabelledby}
      onMouseEnter={onMouseEnterLink}
      onMouseLeave={onMouseLeaveLink}
      onFocus={onFocus}
    >
      {loading && (
        <>
          <Spinner
            visible={true}
            light={linkType !== 'primary'}
            forButton={true}
            styleOverrides={size === 'small' ? { spinner: { transform: 'scale(0.25)' } } : {}}
          />
        </>
      )}
      {/* TODO: Should children have a loading/not-loading state? */}
      {!loading && children}
      {!loading && text}
    </a>
  );
};

export default Link;
